import config from '../../config';
import readyDOM from '../../../utils/dom/readyDOM';
import PrepareWidget from '../../../widgets/prepare-widget';
import { featureAdsPayload } from '../helpers/servicePlanPayload';
import extendApplicant from '../../../widgets/extend-applicant';
import fetchJSON from '../../../utils/fetch/fetchJSON';

/* eslint global-require: 0 */

const widget = new PrepareWidget({
  containerClass: 'hc-sem-featureAds-plans',
  widgetClass: 'hc-sem-featureAds-plan',
  widgetName: 'hc.sem.insertFeatureAdsPlans',
});

/** @override */
widget.buildHTMLPlan = buildHTMLPlan;
widget.getPlans = getPlans;

const {widgetClass} = widget;

let payload;

/**
 * @private
 * Creates a projection of the list of plans only with
 * the properties that will be used by `widget.buildHTMLPlan`
 *
 * @param {Array} plans: the list of plans
 * @returns {Array}
 */
function mapPlans(plans) {
  return plans.map(p => ({
    name: p.AdCreative.Title,
    logo: p.AdCreative.Image,
    displayUrl: p.AdCreative.DisplayUrl,
    redirectUrl: p.Delivery.Click.InterstitialUrl,
  }));
}

/**
 * @private
 * Builds the HTML wrapper for each plan in the list of feature ads plans.
 *
 * @param {Object} plan: the plan data
 * @returns {String}
 */
function buildHTMLPlan(plan) {
  return `<a class="${widgetClass}" href="${plan.redirectUrl}" target="_blank">
      <div class="${widgetClass}__body">
        <img class="${widgetClass}__logo" src="${plan.logo}">
        <div class="${widgetClass}__content">
          <div class="${widgetClass}__name" title="${plan.name}">${plan.name}</div>
          <div class="${widgetClass}__url">${plan.displayUrl}</div>
        </div>
      </div>
      <div class="${widgetClass}__cta">
        <button type="button" class="${widgetClass}__button shadowed">View Details</button>
      </div>
    </a>`;
}

/**
   * set the size page
   * @param {Object} p the payload
   */
function setPayload(p) {
  payload = p;
}

/**
   * set the geolocation
   * @param {Object} geoLocation the geolocation based on the IP
   * @returns {Promise}
   */
function setGeoLocation(geoLocation) {
  return Object.assign(payload, {
    hc_state: geoLocation.stateAcronym,
    hc_zip: geoLocation.zipCode,
  });
}

/**
   * set the size page
   * @param {Object} applicant the payload
   * @param {Number} sizePage How many plans do you want to get
   * @returns {Promise}
   */
function setSizePage(applicant, sizePage) {
  return Object.assign(applicant, { hc_maxregs: sizePage });
}

/**
   * set product
   * @param {Object} applicant the payload
   * @param {String} product the product
   * @returns {Promise}
   */
function setProduct(applicant, product) {
  return {
    ...applicant,
    product,
    hc_product_type: product === 'HEALTH' ? 1 : 2,
  };
}

/**
   * set the size page
   * @param {Number} applicant How many plans do you want to get
   * @param {Function} payloadReducer Function to alter the final payload sent to API
   * @returns {Promise}
   */
function getPayloadData(applicant, payloadReducer) {
  if (payloadReducer) {
    return { data: payloadReducer(applicant) };
  }
  return { data: applicant };
}

/**
   * Get plans from the given URL.
   *
   * @param {String} url: the url of the endpoint
   * @param {Number} howMany: How many plans do you want to get
   * @param {String} product: the product
   * @param {String} zipCode: override the zipCode
   * @param {Function} payloadReducer: Function to alter the final payload sent to API
   * @returns {Promise}
   * @memberof PrepareWidget
   */
function getPlans(url, howMany = 1, product = 'HEALTH', zipCode = '', payloadReducer) {
  return extendApplicant.getGeoLocation(zipCode)
    .then(geoLocation => setGeoLocation(geoLocation))
    .then(applicant => setProduct(applicant, product))
    .then(applicant => setSizePage(applicant, howMany))
    .then(applicant => fetchJSON(url, getPayloadData(applicant, payloadReducer)));
}

/**
 * @public
 * Inserts the HTML block which renders feature ads plans when the DOM is ready.
 *
 * @param {Object} options: An Object which contains the following options:
 *        - {String} title: text that will be heading plans widget
 *        - {String} disclaimer: disclaimer text, placed at the bottom
 *        - {String} divId: element id where the HTML will be inserted
 *        - {Number} howMany: How many plans should be inserted
 *        - {Function} payloadReducer: Function to alter the final payload sent to API
 * @throws {Error} if the element with `divId` does not exist.
 */
export default function insertRotationToolsAds(options = {}) {
  options.css = require('./style.scss').toString();
  setPayload(featureAdsPayload);
  const div = widget.configure(options);
  Promise.all([
    readyDOM(),
    widget.getPlans(`${config.featureAds}`, options.howMany, options.product, options.zipCode, options.payloadReducer),
  ]).then((responses) => {
    if (!responses[1].Result) return [];
    const { Result } = responses[1];
    div.innerHTML = widget.buildHTMLBlock(mapPlans(Result));
    return Result;
  });
}
